import { ClockIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { BL_Warranty, nestApiGetAllMainGroups } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import {
  AssociatedPartner,
  DTBusiness_User_Invite,
  DTHoldingProxy,
  DTMainGroup,
  DTRetailer_Public,
  FBD_Business_User_Invite,
  FBD_Retailer_Public,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  SELF_REGISTERED_VENDABLE,
  TenantInfo,
  WarrantyStatus,
} from '@rabbit/data/types';
import {
  DR_Business_User_Invite,
  DR_Retailer_Public,
} from '@rabbit/data/portal';
import { getDocs, query, where } from 'firebase/firestore';
import {
  ButtonIcon,
  FiltersModal,
  PillTime,
  Table,
  getClaimLimit,
  getCurrencyFormat,
  getWarrantyTerm,
  timestampToTimeAgo,
} from '@rabbit/elements/shared-components';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { RegistrationTableColumns } from '@rabbit/sage/utils/RegistrationTableColumns';
import {
  getProductTitleFromHoldingData,
  getWarrantyRemainingByDate,
  isValidDate,
  ONCE,
} from '@rabbit/sage/utils/helpers';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { Config_RegistrationTableType } from 'libs/configs/src/types/config-types';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MRT_ColumnFiltersState } from 'material-react-table';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { UserContext } from '@rabbit/sage/context/UserContext';

export interface TableRegistrationsProps {
  columns?: any;
  isLoading?: boolean;
  tenantType?: string;
  holdingList?: any;
  filters: FormValues;
  globalFilter?: string;
  setGlobalFilter?: Dispatch<React.SetStateAction<string>>;
  onFilter?: (value: FormValues) => void;
  isRegularTenant?: boolean;
  onRetailerDataLoaded?: (
    retailerData: DTRetailer_Public[],
    businessUserData: DTBusiness_User_Invite[]
  ) => void;
  manualPagination?: boolean;
  rowCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPageChange?: (pageIndex: number, pageSize: number) => void;
  manualSorting?: boolean;
  sorting?: any[];
  onSortChange?: Dispatch<React.SetStateAction<any>>;
  manualSearch?: boolean;
  // New props to support server-side column filtering via Algolia
  columnFilters?: MRT_ColumnFiltersState;
  onColumnFiltersChange?: (
    updater: SetStateAction<MRT_ColumnFiltersState>
  ) => void;
  /**
   * Optional: when provided (from RegistrationsViewV2), export-all will fetch ALL holdings from Algolia
   * and TableRegistrations will format them for CSV export.
   */
  onRequestExportAllData?: (
    onProgress?: (loaded: number, total: number) => void
  ) => Promise<DTHoldingProxy[]>;
}

export interface FormValues {
  voided?: boolean;
  dateType?: string;
  beginDate?: Date | undefined;
  endDate?: Date | undefined;
}

function TableRegistrations({
  isLoading,
  tenantType,
  holdingList,
  filters,
  onFilter,
  isRegularTenant = false,
  onRetailerDataLoaded,
  manualPagination = false,
  rowCount,
  pageIndex,
  pageSize,
  onPageChange,
  globalFilter,
  setGlobalFilter,
  manualSorting = false,
  sorting,
  onSortChange,
  manualSearch = false,
  columnFilters,
  onColumnFiltersChange,
  onRequestExportAllData,
}: TableRegistrationsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const onStart = () => setIsExporting(true);
    const onEnd = () => setIsExporting(false);
    window.addEventListener('registrations-export-start', onStart as any);
    window.addEventListener('registrations-export-end', onEnd as any);
    return () => {
      window.removeEventListener('registrations-export-start', onStart as any);
      window.removeEventListener('registrations-export-end', onEnd as any);
    };
  }, []);
  const userContext = useContext(UserContext);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [mainGroups, setMainGroups] = useState<Map<string, string>>(new Map());
  const { config, tenantInfo, algoliaConfig } = useContext(AppContext);
  const { activeTenantInfo } = useContext(UserContext) ?? {};
  const { warrantorPersona, manufacturerPersona } = useGetMySagePersonas();
  const tenantLink = t('tenantLink');
  const isAdmin = userContext?.accessToken?.admin ?? false;

  const isWarrantor = warrantorPersona !== null;
  const columns = useMemo(
    () => RegistrationTableColumns.Default(t, config),
    [t]
  );
  const carColumns = useMemo(
    () =>
      RegistrationTableColumns.CarColumns(
        config,
        !!warrantorPersona?.personaId,
        isAdmin
      ),
    [config, warrantorPersona, isAdmin]
  );
  const tableTopColumns = useMemo(
    () => RegistrationTableColumns.TableTop(t, isWarrantor),
    [t, isWarrantor]
  );
  const supagardColumns = useMemo(
    () =>
      RegistrationTableColumns.Supagard(
        t,
        Boolean(manufacturerPersona?.manufacturer_private?.docid),
        config.HOLDINGS.REGISTRATION.SHOW_DEALER_GROUP
      ),
    [t, manufacturerPersona]
  );

  const getColumns = () => {
    let baseColumns: any[] = [];

    if (tenantLink === 'SUPAGARD') {
      baseColumns = supagardColumns;

      // only for SUPAGARD → disable sorting if not in algoliaConfig.sorting
      const algoliaSortableKeys = Object.keys(algoliaConfig?.sorting || {});
      const mapped = baseColumns.map((col: any) => ({
        ...col,
        enableSorting: algoliaSortableKeys.includes(col.accessorKey),
      }));

      return mapped;
    }

    switch (tenantType) {
      case 'carTenants':
        {
          /* // Price has been temporarily removed per DEV-1128 request -VP */
        }
        if (tenantLink === 'WARRANTYIRELAND') {
          return isRegularTenant
            ? carColumns.filter(
                (column: any) =>
                  column.accessorKey !== 'partner' &&
                  column.accessorKey !== 'price'
              )
            : carColumns.filter(
                (column: any) =>
                  column.accessorKey !== 'salesPerson' &&
                  column.accessorKey !== 'price'
              );
        } else {
          return isRegularTenant
            ? carColumns.filter(
                (column: any) => column.accessorKey !== 'partner'
              )
            : carColumns.filter(
                (column: any) => column.accessorKey !== 'salesPerson'
              );
        }
      case 'tableTenants':
        return isRegularTenant
          ? tableTopColumns.filter(
              (column: any) => column.accessorKey !== 'partner'
            )
          : tableTopColumns;
      default:
        return isRegularTenant
          ? columns.filter((column: any) => column.accessorKey !== 'partner')
          : columns;
    }
  };

  const onChangeFilters = (value: {
    beginDate?: Date | undefined;
    endDate?: Date | undefined;
  }) => {
    setIsFilterModalOpen(false);
    if (onFilter) {
      onFilter(value);
    }
  };

  const BikeRegistrationTable = useMemo(
    () =>
      CreateTable<DTHoldingProxy>(holdingList.data || [], [
        {
          header: 'Timestamp',
          Header: <ClockIcon className="h-4 w-4" />,
          Cell: ({ cell }: any) => {
            return (
              <PillTime
                label={
                  cell.getValue() ? timestampToTimeAgo(cell.getValue()) : ''
                }
                className={'bg-gray-300 text-gray-900'}
              />
            );
          },
          value: (holding) => holding.tcreate,
        },
        ...(config.HOLDINGS.REGISTRATION_DETAILS_VIEW.COMPANY_NAME
          ? [
              {
                header: t('general.businessName'),
                value: (holding: any) => holding.business_name ?? '-',
              },
            ]
          : []),
        {
          header: t('general.id'),
          value: (holding) => holding.docid?.slice(0, 5).toUpperCase(),
        },
        {
          header: t('general.customerName'),
          value: (holding) => holding.consumer_name,
        },
        {
          header: t('general.product'),
          primary: true,
          value: (holding) => getProductTitleFromHoldingData(holding),
        },
        ...(config.HOLDINGS.REGISTRATION.SHOW_TABLE_SERIAL_NUMBER
          ? [
              {
                header: t('general.serialNumber'),
                value: (holding: any) => holding.serial || '-',
              },
            ]
          : []),
        {
          header: t('general.modelNumber'),
          value: (holding) =>
            holding?.mpn && holding?.mpn !== 'n/a' ? holding.mpn : '-',
        },
        ...(config.HOLDINGS.REGISTRATION.SHOW_CHASSIS_NUMBER
          ? [
              {
                header: t('general.chassis_number'),
                value: (holding: any) => holding.chassis_number || '-',
              },
            ]
          : []),
        ...(config.HOLDINGS.REGISTRATION.SHOW_REGISTRATION_NUMBER
          ? [
              {
                header: t('general.registration_number'),
                value: (holding: any) => holding.registration_number || '-',
              },
            ]
          : []),
        {
          header: t('general.purchaseDate'),
          type: 'Date',
          value: (holding) => holding.purchase_time || '-',
        },
        {
          header: t('general.registered'),
          type: 'Date',
          value: (holding) => holding.register_time || '-',
        },
        ...(config.HOLDINGS.REGISTRATION_DETAILS_VIEW.PURCHASE_PRICE
          ? [
              {
                header: t('general.purchasePrice'),
                value: (holding: any) =>
                  holding.decider?.decided?.totalPrice
                    ? getCurrencyFormat(
                        holding.decider?.decided?.totalPrice as number,
                        tenantInfo?.currency as string
                      )
                    : '-',
              },
            ]
          : []),
        {
          header: t('general.expiring'),
          type: 'Date',
          value: (holding) =>
            BL_Warranty.getLatestWarranty(holding.warranties)?.endDate || '-',
        },
        {
          header: t('general.termLeft'),
          value: (holding) => {
            const warranty = BL_Warranty.getLatestWarranty(holding.warranties);
            return warranty?.endDate && warranty.startDate
              ? getWarrantyRemainingByDate(
                  warranty?.endDate,
                  warranty?.startDate
                )
              : '-';
          },
        },
      ]),
    [holdingList.data, t]
  );

  const CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT =
    t('CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!isLoading && holdingList.data) {
          const getTableData = async () => {
            const allDealerShips = config.HOLDINGS.REGISTRATION.SHOW_DEALER_NAME
              ? await getHoldingDealerships(holdingList?.data)
              : null;

            switch (tenantType) {
              case 'carTenants':
                return formatCarResults(
                  holdingList?.data,
                  CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT,
                  tenantInfo
                ).filter((i) => i !== null);

              case 'tableTenants':
                return formatTableCoverResults(
                  holdingList?.data,
                  tenantInfo
                ).filter((i) => i !== null);

              default:
                return formatResults(
                  holdingList?.data,
                  config,
                  tenantLink,
                  mainGroups,
                  activeTenantInfo?.info?.name,
                  allDealerShips
                ).filter((i) => i !== null);
            }
          };

          const result = await getTableData();
          setTableData(result);
        }
      } catch (error) {
        console.error('Error fetching registration table data:', error);
        setTableData([]);
        // Optionally show UI error state or notification
      }
    };

    fetchData();
  }, [holdingList.data, isLoading, t, mainGroups]);

  // Function to create dealer map from pre-fetched data
  const getHoldingDealerships = async (holdingList: DTHoldingProxy[]) => {
    const dealerMap = new Map();
    try {
      const purchaseLocations = new Set<string>();
      for (const holding of holdingList || []) {
        if (holding?.purchase_location) {
          purchaseLocations.add(holding.purchase_location);
        }
      }

      if (purchaseLocations.size === 0) return dealerMap;

      const retailerIds: string[] = [];
      const businessUserIds: string[] = [];

      purchaseLocations.forEach((id) => {
        if (id.includes(':')) {
          retailerIds.push(id);
        } else {
          businessUserIds.push(id);
        }
      });

      const fetchInChunks = async (
        ids: string[],
        collectionRef: any,
        idField: string,
        callback: (doc: any) => void
      ) => {
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) {
          chunks.push(ids.slice(i, i + 10));
        }

        const fetchChunk = async (chunk: string[]): Promise<void> => {
          if (chunk.length === 0) return;
          const q = query(collectionRef, where(idField, 'in', chunk));
          const snapshot = await getDocs(q);
          snapshot.docs.forEach((doc) => callback(doc.data()));
        };

        await Promise.all(chunks.map((chunk) => fetchChunk(chunk)));
      };

      const retailerDataLoaded: DTRetailer_Public[] = [];
      const businessUserDataLoaded: DTBusiness_User_Invite[] = [];

      if (retailerIds.length > 0) {
        await fetchInChunks(
          retailerIds,
          DR_Retailer_Public.collection(),
          'docid',
          (data) => {
            dealerMap.set(data.docid, data.name);
            retailerDataLoaded.push(data);
          }
        );
      }

      if (businessUserIds.length > 0) {
        await fetchInChunks(
          businessUserIds,
          DR_Business_User_Invite.collection(),
          'docid',
          (data) => {
            dealerMap.set(data.docid, data.orgName);
            businessUserDataLoaded.push(data);
          }
        );
      }

      if (onRetailerDataLoaded) {
        onRetailerDataLoaded(retailerDataLoaded, businessUserDataLoaded);
      }
    } catch (error) {
      console.error('Error in getHoldingDealerships:', error);
    }

    return dealerMap;
  };

  useEffect(() => {
    const getMainGroups = async () => {
      const mainGroupList: any = await nestApiGetAllMainGroups();
      const mainGroupSet = new Map<string, string>();
      if (mainGroupList.length > 0) {
        mainGroupList.forEach((item: DTMainGroup) => {
          mainGroupSet.set(item.docid, item.title);
          item.associatedPartners?.forEach((partners: AssociatedPartner) => {
            mainGroupSet.set(
              partners.tenantLink.length > 0
                ? partners.tenantLink
                : partners.businessUserInviteLink,
              item.title
            );
          });
        });
      }
      setMainGroups(mainGroupSet);
    };

    getMainGroups();
  }, [config.HOLDINGS.REGISTRATION.SHOW_DEALER_GROUP]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="w-full">
        <div className="relative z-10 flex w-full items-center justify-between">
          {!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE && (
            <div className="relative pb-4 md:absolute md:top-[25px] md:right-5">
              <ButtonIcon
                label={t('general.filters')}
                iconLeft={true}
                Icon={FunnelIcon}
                onClick={() => setIsFilterModalOpen(true)}
                kind={'bgLightGreen'}
                count={
                  (filters && filters.beginDate) || (filters && filters.endDate)
                    ? 1
                    : 0
                }
              />
            </div>
          )}

          {config.HOLDINGS.REGISTRATION.ADVANCED_TABLE &&
            config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA && (
              <div
                className={`relative pb-4 md:absolute  md:top-[20px] ${
                  isExporting ? 'md:left-[155px]' : 'md:left-[140px]'
                }`}
              >
                <ButtonIcon
                  label={t('general.filters')}
                  iconLeft={true}
                  Icon={FunnelIcon}
                  onClick={() => setIsFilterModalOpen(true)}
                  kind={'bgLightGreen'}
                  count={
                    (filters && filters.beginDate) ||
                    (filters && filters.endDate)
                      ? 1
                      : 0
                  }
                />
              </div>
            )}

          {isFilterModalOpen && (
            <div
              className={`absolute ${
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE &&
                config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA
                  ? 'left-0'
                  : 'right-0'
              } top-16 z-10`}
            >
              <FiltersModal
                page={'registrations'}
                handleClose={() => setIsFilterModalOpen(false)}
                data={filters}
                onChange={onChangeFilters}
              />
            </div>
          )}
        </div>

        {config.HOLDINGS.REGISTRATION.TABLE_TYPE ===
          Config_RegistrationTableType.BIKE_TYPE && (
          <div className="relative z-0">
            <BikeRegistrationTable
              initialState={{
                columnPinning: { right: ['actions'] },
                showGlobalFilter: true,
                sorting: [
                  {
                    id: 'timestamp',
                    desc: true,
                  },
                ],
              }}
              muiSearchTextFieldProps={{
                placeholder: t('general.searchByRegistrationID'),
              }}
              enableColumnFilters={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableTopToolbar={!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
              advancedTable={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableRowSelection={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableBottomToolbar={true}
              enableToolbarInternalActions={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableFacetedValues={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              columnFilterDisplayMode="popover"
              state={{
                isLoading: isLoading,
              }}
              onClickRow={(holding) =>
                ONCE(
                  () =>
                    navigate(
                      SAGE_ROUTE_NAME.REGISTRATIONS + '/' + holding.docid,
                      {
                        state: {
                          warrantyId: holding.docid.slice(0, 5).toUpperCase(),
                          title: getProductTitleFromHoldingData(holding),
                          link: holding.docid,
                        },
                      }
                    ),
                  2000
                )()
              }
            />
          </div>
        )}

        {config.HOLDINGS.REGISTRATION.TABLE_TYPE ===
          Config_RegistrationTableType.DEFAULT && (
          <div className="relative z-0">
            <Table
              columns={getColumns()}
              data={tableData}
              initialState={{
                columnPinning: { right: ['actions'] },
                showGlobalFilter: true,
                sorting: [{ id: 'timeStamp', desc: false }],
                ...(manualPagination && {
                  pagination: {
                    pageIndex: pageIndex ?? 0,
                    pageSize: pageSize ?? 10,
                  },
                }),
              }}
              manualPagination={manualPagination}
              manualSorting={manualSorting}
              rowCount={manualPagination ? rowCount : undefined}
              state={{
                isLoading: isLoading,
                ...(manualPagination && {
                  pagination: {
                    pageIndex: pageIndex ?? 0,
                    pageSize: pageSize ?? 10,
                  },
                }),
                ...(manualSorting && { sorting: sorting }),
                ...(columnFilters ? { columnFilters } : {}),
              }}
              onPaginationChange={
                manualPagination
                  ? (updater: any) => {
                      try {
                        const currentState = {
                          pageIndex: pageIndex ?? 0,
                          pageSize: pageSize ?? 10,
                        };
                        const next =
                          typeof updater === 'function'
                            ? updater(currentState)
                            : updater;
                        if (onPageChange)
                          onPageChange(next.pageIndex, next.pageSize);
                      } catch (e) {
                        console.error('Error onPaginationChange:', e);
                      }
                    }
                  : undefined
              }
              manualSearch={manualSearch}
              globalFilter={globalFilter}
              onGlobalFilterChange={setGlobalFilter}
              {...(onSortChange ? { onSortChange } : {})}
              {...(onColumnFiltersChange ? { onColumnFiltersChange } : {})}
              enableColumnFilters={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableTopToolbar={!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
              advancedTable={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableRowSelection={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableBottomToolbar={true}
              enableToolbarInternalActions={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableFacetedValues={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              columnFilterDisplayMode="popover"
              muiSearchTextFieldProps={{
                placeholder: t('general.search'),
              }}
              useCallbackClickRow={(data: {
                warrantyId: string;
                product: string;
                registrationPlate: string;
                make: string;
                link: string;
                id: string;
                modelNum: string;
              }) =>
                ONCE(
                  () =>
                    navigate(SAGE_ROUTE_NAME.REGISTRATIONS + '/' + data.link, {
                      state: {
                        warrantyId:
                          tenantType === 'tableTenants'
                            ? data.id
                            : data.warrantyId,
                        title:
                          tenantType === 'carTenants'
                            ? data.make + ' ' + data.registrationPlate
                            : data.product,
                        link: data.link,
                        modelNum: data.modelNum,
                      },
                    }),
                  2000
                )()
              }
              onRequestAllData={async (
                onProgress?: (loaded: number, total: number) => void
              ) => {
                try {
                  if (!onRequestExportAllData) return tableData;
                  const allHoldings = await onRequestExportAllData(onProgress);
                  const allDealerShips = config.HOLDINGS.REGISTRATION
                    .SHOW_DEALER_NAME
                    ? await getHoldingDealerships(allHoldings)
                    : null;
                  switch (tenantType) {
                    case 'carTenants':
                      return formatCarResults(
                        allHoldings,
                        CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT,
                        tenantInfo
                      ).filter((i) => i !== null);
                    case 'tableTenants':
                      return formatTableCoverResults(
                        allHoldings,
                        tenantInfo
                      ).filter((i) => i !== null);
                    default:
                      return formatResults(
                        allHoldings,
                        config,
                        tenantLink,
                        mainGroups,
                        activeTenantInfo?.info?.name,
                        allDealerShips
                      ).filter((i) => i !== null);
                  }
                } catch (e) {
                  console.error('Error preparing all-data export:', e);
                  return tableData;
                }
              }}
            />
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
}

export default TableRegistrations;

/** HELPERS **/

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatResults = (
  results: any[],
  config: any,
  tenantType: string,
  mainGroupSets: Map<string, string>,
  defaultGroup?: string,
  allDealerShips?: Map<string, string> | null
) => {
  const formattedResults = results.map((result, i) => {
    if (result.vendable === SELF_REGISTERED_VENDABLE) return null;
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);

    let serialNumber = result?.serial || '-';

    if (config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) {
      const isSerialNumberVerified = result?.serialNumberVerified === true;
      serialNumber = isSerialNumberVerified
        ? serialNumber
        : serialNumber + ' (Unverified)';
    }

    return {
      timeStamp: (
        <PillTime
          label={
            result.register_time
              ? timestampToTimeAgo(result?.register_time)
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      warrantyId: result.docid.slice(0, 5).toUpperCase(),
      registered: isValidDate(new Date(result?.register_time))
        ? result.register_time
        : undefined,
      expiring: warrantyInfo?.endDate,
      term: getWarrantyTerm(result),
      product: getProductTitleFromHoldingData(result),
      modelNum: result?.mpn && result?.mpn !== 'n/a' ? result.mpn : '-',
      serialNum: serialNumber,
      purchaseDate:
        result?.purchase_time && result?.purchase_time > 0
          ? result?.purchase_time
          : undefined,
      extended: 'No', //TODO
      // actions: (
      //   <div className="flex items-center justify-center">
      //     <TrashIcon
      //       className="w-5 h-5 cursor-pointer"
      //       onClick={() => {
      //         setdeleteRegistration(!deleteRegistration);
      //         // onDeleteRegistration(result.docid);
      //       }}
      //     />
      //   </div>
      // ),
      link: result.docid,
      consumerName: result.consumer_name || '-',
      salesPerson: result?.additionalData?.salesperson || '-',
      dealerGroup: (() => {
        if (!result?.purchase_location) return defaultGroup || '-';

        const locationParts = result.purchase_location.split(':');
        const lookupKey =
          locationParts.length > 1
            ? locationParts[1]
            : result.purchase_location;

        return mainGroupSets.get(lookupKey) || defaultGroup || '-';
      })(),
      registrationNumber: result?.additionalData?.vehicleNo || '-',
      vehicleMake: result?.additionalData?.make || '-',
      vehicleModel: result?.additionalData?.model || '-',
      salesReferenceNumber: result?.additionalData?.salesReferenceNo || '-',
      uniqueCode: result?.additionalData?.uniqueCode || '-',
      leatherUniqueCode: result?.additionalData?.leatherUniqueCode || '-',
      ...(config.HOLDINGS.REGISTRATION.SHOW_DEALER_NAME
        ? { dealerName: allDealerShips?.get(result?.purchase_location) || '-' }
        : {}),
    };
  });
  return formattedResults;
};

const formatCarResults = (
  results: any[],
  CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT: boolean,
  tenantInfo?: TenantInfo
) => {
  return results.map((result, i) => {
    const vehicleInfo =
      result.self_registration?.srvInfo?.productInfo ??
      result.self_registration?.vehicleInfo; // TODO: make it more flexible for other tenants as part of DEV-528
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);
    const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;
    return {
      timeStamp: (
        <PillTime
          label={
            result.tcreate
              ? timestampToTimeAgo(new Date(result?.tcreate).getTime())
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      warrantyId: result.docid.slice(0, 5).toUpperCase(),
      registrationPlate: BL_Warranty.getVehicleRegistrationNo(vehicleInfo),
      make: vehicleInfo.brand || '-',
      registered: result.tcreate,
      start: warrantyInfo?.startDate,
      expiring: warrantyInfo?.endDate,
      warrantyPlan: voided ? 'voided' : warrantyInfo?.templateTitle || '-',
      term: voided ? '-' : getWarrantyTerm(result),
      claimLimit: voided
        ? '-'
        : getClaimLimit(
            warrantyInfo,
            tenantInfo?.currency,
            CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT
          ) || '-',
      partner: warrantyInfo?.retailerName || '-',
      salesPerson: warrantyInfo?.salesRep ?? '',
      price: warrantyInfo?.price
        ? getCurrencyFormat(
            warrantyInfo?.price?.amount,
            warrantyInfo?.price?.currency
          ) +
          ` (${getCurrencyFormat(
            warrantyInfo?.price?.amount * 1.2,
            warrantyInfo?.price?.currency
          )} incl. VAT)`
        : '-',
      link: result.docid,
      customerName: result.consumer_name || '-',
    };
  });
};

const formatTableCoverResults = (results: any[], tenantInfo: any) =>
  results.map((result, i) => {
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);
    const productInfo = result.self_registration.srvInfo.productInfo;
    const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;

    const purchasePrice = warrantyInfo?.decider?.decided?.totalPrice ?? 0;

    return {
      timeStamp: (
        <PillTime
          label={
            result.created_time
              ? timestampToTimeAgo(new Date(result?.created_time).getTime())
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      id: result.docid.slice(0, 5).toUpperCase(),
      product: productInfo.typeofStone,
      area: productInfo.surfaceAreaValue
        ? productInfo.surfaceAreaValue + 'm²'
        : 'N/A',
      registered: result.tcreate,
      start: warrantyInfo?.startDate,
      expiring: warrantyInfo?.endDate,
      warrantyPlan: voided
        ? 'voided'
        : warrantyInfo?.templateTitle?.replace(' warranty', '') || '-',
      term: voided ? '-' : getWarrantyTerm(result),
      partner: warrantyInfo?.retailerName,
      salesperson: warrantyInfo?.salesRep ?? '-',
      purchasePrice: purchasePrice
        ? getCurrencyFormat(purchasePrice as number, tenantInfo?.currency)
        : '0',
      link: result.docid,
      customerName: result.consumer_name || '-',
      sealing: productInfo.upsellSealing ? 'Yes' : 'No',
    };
  });
